/**
 * Fiche produit Dawn V2 : galerie de la section « main-product ».
 *  1. Points de pagination (mobile) quand « Compteur d'images (mobile) » est réglé sur Points.
 *  2. Lecteur vidéo : lecture automatique sans le son, ou bouton de lecture en ligne,
 *     avec bouton pause, bouton du son et barre de progression au choix.
 *     (Le type « Bouton de lecture avec fenêtre » ne demande rien ici : il passe par le CSS.)
 * Les réglages arrivent par des attributs data-* posés sur <product-info> (voir sections/main-product.liquid).
 */
(() => {
  const ICONS = {
    play: '<svg viewBox="0 0 10 14" aria-hidden="true" focusable="false"><path d="M1 1.2v11.6L9.3 7z" fill="currentColor"/></svg>',
    sound:
      '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M3 9v6h4l5 4V5L7 9z" fill="currentColor"/><path d="M15.5 8.5a5 5 0 0 1 0 7M18.5 5.5a9 9 0 0 1 0 13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>',
    muted:
      '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M3 9v6h4l5 4V5L7 9z" fill="currentColor"/><path d="M16 9l5 6M21 9l-5 6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>',
  };

  /* ------------------------------------------------------------------ */
  /* 1. Points de pagination                                            */
  /* ------------------------------------------------------------------ */
  class GalleryDots {
    constructor(root) {
      this.viewer = root.querySelector('media-gallery slider-component[id^="GalleryViewer"]');
      this.buttons = this.viewer?.querySelector('.slider-buttons');
      this.slider = this.viewer?.querySelector('[id^="Slider-Gallery"]');
      if (!this.buttons || !this.slider || this.buttons.querySelector('.v2-gallery-dots')) return;

      this.overlay = root.dataset.galleryPagination === 'dots_overlay';
      this.buttons.classList.add('v2-gallery-buttons--dots');
      if (this.overlay) this.buttons.classList.add('v2-gallery-buttons--overlay');

      this.dots = document.createElement('div');
      this.dots.className = 'v2-gallery-dots';
      this.dots.setAttribute('role', 'group');
      const counter = this.buttons.querySelector('.slider-counter');
      if (counter) counter.after(this.dots);
      else this.buttons.append(this.dots);

      this.build();
      this.slider.addEventListener('scroll', () => this.update(), { passive: true });
      new MutationObserver(() => this.build()).observe(this.slider, { childList: true });
      new ResizeObserver(() => this.update()).observe(this.slider);
    }

    get items() {
      return Array.from(this.slider.querySelectorAll('.product__media-item')).filter((item) => item.clientWidth > 0);
    }

    build() {
      const items = this.items;
      this.dots.replaceChildren(
        ...items.map((item, index) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'v2-gallery-dots__dot';
          dot.setAttribute('aria-label', `${index + 1} / ${items.length}`);
          dot.innerHTML = '<span></span>';
          dot.addEventListener('click', () => this.goTo(index));
          return dot;
        })
      );
      this.update();
    }

    goTo(index) {
      const items = this.items;
      if (!items[index]) return;
      this.slider.scrollTo({ left: items[index].offsetLeft - items[0].offsetLeft, behavior: 'smooth' });
    }

    update() {
      const items = this.items;
      if (items.length < 2) return;
      const step = items[1].offsetLeft - items[0].offsetLeft;
      const current = Math.min(items.length - 1, Math.max(0, Math.round(this.slider.scrollLeft / step)));
      Array.from(this.dots.children).forEach((dot, index) => {
        dot.classList.toggle('is-active', index === current);
        if (index === current) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }
  }

  /* ------------------------------------------------------------------ */
  /* 2. Lecteur vidéo                                                   */
  /* ------------------------------------------------------------------ */
  class GalleryVideos {
    constructor(root) {
      this.mode = root.dataset.videoPlayer;
      if (this.mode !== 'autoplay' && this.mode !== 'play_btn') return;
      this.showPause = root.dataset.videoPauseBtn === 'true';
      this.showSound = root.dataset.videoSoundBtn === 'true';
      this.showTimeline = root.dataset.videoTimeline === 'true';
      root.querySelectorAll('.media-type-video deferred-media').forEach((media) => this.setup(media));
    }

    setup(media) {
      const attach = () => {
        const video = media.querySelector(':scope > video');
        if (video && !video.dataset.v2Player) this.enhance(media, video);
      };
      new MutationObserver(attach).observe(media, { childList: true });
      attach();

      if (this.mode === 'autoplay' && !media.hasAttribute('loaded')) {
        const observer = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            observer.disconnect();
            this.load(media);
          },
          { rootMargin: '200px' }
        );
        observer.observe(media);
      }
    }

    /** Charge la vidéo cachée dans <template> (sans passer par window.pauseAllMedia, pour ne pas arrêter les autres). */
    load(media) {
      const source = media.querySelector('template')?.content.querySelector('video');
      if (!source || media.hasAttribute('loaded')) return;
      media.setAttribute('loaded', 'true');
      media.appendChild(source.cloneNode(true));
    }

    enhance(media, video) {
      video.dataset.v2Player = 'true';
      const autoplay = this.mode === 'autoplay';
      video.controls = false;
      video.playsInline = true;
      video.setAttribute('disablepictureinpicture', '');
      if (autoplay) {
        video.loop = true;
        video.muted = true;
        video.autoplay = true;
      }

      const ui = document.createElement('div');
      ui.className = 'v2-video';
      media.appendChild(ui);

      // Bouton de lecture / pause au centre : seul moyen d'arrêter une vidéo en lecture automatique.
      const canToggle = !autoplay || this.showPause;
      let play = null;
      if (canToggle) {
        play = document.createElement('button');
        play.type = 'button';
        play.className = 'v2-video__play';
        play.setAttribute('aria-label', 'Lecture / pause');
        play.innerHTML = ICONS.play;
        ui.appendChild(play);
        const toggle = () => {
          if (video.paused) {
            video.dataset.userPaused = '';
            video.play().catch(() => {});
          } else {
            video.dataset.userPaused = 'true';
            video.pause();
          }
        };
        play.addEventListener('click', toggle);
        video.addEventListener('click', toggle);
      }
      const syncPlay = () => ui.classList.toggle('is-paused', video.paused);
      video.addEventListener('play', syncPlay);
      video.addEventListener('pause', syncPlay);
      video.addEventListener('ended', syncPlay);
      syncPlay();

      if (this.showSound) {
        const sound = document.createElement('button');
        sound.type = 'button';
        sound.className = 'v2-video__sound';
        sound.setAttribute('aria-label', 'Son');
        const syncSound = () => {
          sound.innerHTML = video.muted ? ICONS.muted : ICONS.sound;
          sound.setAttribute('aria-pressed', String(!video.muted));
        };
        sound.addEventListener('click', () => {
          video.muted = !video.muted;
          syncSound();
        });
        syncSound();
        ui.appendChild(sound);
        ui.classList.add('has-sound');
      }

      if (this.showTimeline) {
        const timeline = document.createElement('span'); // un span : Dawn cache les div vides
        timeline.className = 'v2-video__timeline';
        timeline.setAttribute('role', 'slider');
        timeline.setAttribute('aria-label', 'Progression de la vidéo');
        timeline.setAttribute('aria-valuemin', '0');
        timeline.setAttribute('aria-valuemax', '100');
        ui.appendChild(timeline);
        const sync = () => {
          const done = video.duration ? (video.currentTime / video.duration) * 100 : 0;
          timeline.style.setProperty('--completed', `${done}%`);
          timeline.setAttribute('aria-valuenow', String(Math.round(done)));
        };
        video.addEventListener('timeupdate', sync);
        video.addEventListener('loadedmetadata', sync);
        const seek = (event) => {
          const box = timeline.getBoundingClientRect();
          if (!video.duration || !box.width) return;
          const ratio = Math.min(1, Math.max(0, (event.clientX - box.left) / box.width));
          video.currentTime = ratio * video.duration;
          sync();
        };
        timeline.addEventListener('pointerdown', (event) => {
          timeline.setPointerCapture(event.pointerId);
          seek(event);
          const move = (moveEvent) => seek(moveEvent);
          timeline.addEventListener('pointermove', move);
          timeline.addEventListener(
            'pointerup',
            () => {
              timeline.removeEventListener('pointermove', move);
            },
            { once: true }
          );
        });
        ui.classList.add('has-timeline');
      }

      if (autoplay) {
        // La page peut mettre toutes les vidéos en pause (changement d'image) : on reprend dès qu'elle est visible.
        video.addEventListener('pause', () => {
          if (video.ended || video.dataset.userPaused === 'true') return;
          window.setTimeout(() => {
            if (video.paused && video.dataset.userPaused !== 'true' && this.isVisible(video)) video.play().catch(() => {});
          }, 50);
        });
        const observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && video.dataset.userPaused !== 'true') video.play().catch(() => {});
            else if (!entry.isIntersecting && !video.paused) video.pause();
          });
        });
        observer.observe(video);
        video.play().catch(() => {});
      }
    }

    isVisible(element) {
      const box = element.getBoundingClientRect();
      return box.width > 0 && box.bottom > 0 && box.top < window.innerHeight && box.right > 0 && box.left < window.innerWidth;
    }
  }

  /* ------------------------------------------------------------------ */
  function init(root) {
    if (!root || root.dataset.v2Gallery) return;
    root.dataset.v2Gallery = 'true';
    if (root.dataset.galleryPagination?.startsWith('dots')) new GalleryDots(root);
    new GalleryVideos(root);
  }

  const start = () => document.querySelectorAll('product-info[data-video-player]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
  // Éditeur de thème : la section est rechargée à chaque changement.
  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('product-info[data-video-player]').forEach(init);
  });
})();
